import type { SafetyAction, UpsertActionInput, EntityType, WorkflowType } from '@communities/content-management/domain';
import { useUpsertCommunityAction, useUpsertCommunityGroupAction, useCommunityPolicies } from '@communities/content-management/hooks';
import { isCommunity } from '@communities/content-management/utils';
import { ErrorBanner } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { LoadingSpinner, TextInput, TextArea, MultiSelect, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect, useMemo, useCallback } from 'react';

import * as styles from './ActionDialog.css';

interface ActionDialogProps {
  action?: SafetyAction | undefined;
  entityId: string;
  entityType: EntityType;
  workflowType: WorkflowType;
  isOpen: boolean;
  onClose: () => void;
}

// eslint-disable-next-line complexity
export function ActionDialog({ action: initialAction, entityId, entityType, workflowType, isOpen, onClose }: ActionDialogProps) {
  const isAdvancedSettingsEnabled = useIsFeatureEnabled(FEATURE_FLAGS.ACTION_ADVANCED_SETTINGS);
  const [currentAction, setCurrentAction] = useState<SafetyAction | undefined>(initialAction);

  const communityHook = useUpsertCommunityAction(isCommunity(entityType) ? entityId : undefined);
  const communityGroupHook = useUpsertCommunityGroupAction(!isCommunity(entityType) ? entityId : undefined);

  const { upsertAction, loading: mutating, errors, resetErrors } = isCommunity(entityType) ? communityHook : communityGroupHook;
  // Filter policies by workflowType to show only matching policies
  const { policies, loading: policiesLoading, errors: policiesErrors } = useCommunityPolicies(isCommunity(entityType) ? entityId : undefined, workflowType);

  useEffect(() => {
    if (isOpen) {
      resetErrors();
      setCurrentAction(initialAction);
    }
  }, [initialAction, isOpen, resetErrors]);

  // Transform policies to MultiSelect options
  const policyOptions = useMemo(
    () =>
      policies.map(policy => ({
        id: policy.id,
        label: policy.name,
        value: policy.id,
      })),
    [policies]
  );

  // Handle field updates from the content component
  const handleFieldChange = useCallback(
    (field: keyof SafetyAction, value: unknown) => {
      // eslint-disable-next-line complexity
      setCurrentAction(prev => ({
        name: prev?.name || '',
        description: prev?.description || '',
        policies: prev?.policies || [],
        operatorName: prev?.operatorName || '',
        deliverySettings: {
          url: prev?.deliverySettings?.url || '',
          postPayload: prev?.deliverySettings?.postPayload || '',
        },
        ...prev,
        deliveryType: 'http',
        workflowType: prev?.workflowType || workflowType,
        [field]: value,
      }));
    },
    [workflowType]
  );

  // eslint-disable-next-line complexity
  const handleSubmit = async () => {
    resetErrors();

    const input: UpsertActionInput = {
      type: currentAction?.name || '', // BFF expects action name in 'type' field
      description: currentAction?.description || '',
      deliveryType: 'http',
      policies: currentAction?.policies || [],
      operatorName: currentAction?.operatorName || '',
      deliverySettings: {
        url: currentAction?.deliverySettings?.url || '',
        postPayload: currentAction?.deliverySettings?.postPayload || '',
      },
      workflowType,
    };

    const success = await upsertAction(entityId, currentAction?.name || '', input);

    if (success) {
      onClose();
    }
  };

  return (
    <Dialog
      description={initialAction ? 'Update action settings' : 'Configure a new action'}
      isOpen={isOpen}
      primaryAction={{
        label: mutating ? <LoadingSpinner /> : initialAction ? 'Update Action' : 'Create Action',
        onClick: handleSubmit,
        disabled: mutating || !currentAction?.name.trim(),
      }}
      size="xl"
      title={initialAction ? 'Edit Action' : 'Add Action'}
      onClose={onClose}
    >
      <div className={styles.content}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the action:" />}
        {policiesErrors && policiesErrors.length > 0 && <ErrorBanner errors={policiesErrors} title="Error loading policies:" />}

        {/* Name Input */}
        <TextInput
          required
          disabled={!!initialAction} // Only disable name field when editing existing actions
          id="actionName"
          label="Action Name"
          placeholder="Enter action name"
          value={currentAction?.name || ''}
          onChange={value => handleFieldChange('name', value)}
        />

        {/* Description Input */}
        <TextArea
          id="description"
          placeholder="Enter action description"
          title="Description"
          value={currentAction?.description || ''}
          onChange={e => handleFieldChange('description', e.target.value)}
        />

        {/* Policies Multi Select */}
        <MultiSelect
          disabled={mutating}
          error={policiesErrors ? 'Error loading policies' : undefined}
          loading={policiesLoading}
          options={policyOptions}
          placeholder="No policies selected"
          selectedValues={currentAction?.policies || []}
          title="Policies"
          onSelectionChange={(policies: string[]) => handleFieldChange('policies', policies)}
        />

        {/* Delivery Settings Input */}
        <TextInput
          required
          id="url"
          label="URL"
          placeholder="https://example.com/http"
          value={currentAction?.deliverySettings?.url || ''}
          onChange={value =>
            handleFieldChange('deliverySettings', {
              ...currentAction?.deliverySettings,
              url: value,
              postPayload: currentAction?.deliverySettings?.postPayload || '',
            })
          }
        />

        {/* Post Payload Template Input - Feature flagged */}
        {isAdvancedSettingsEnabled && (
          <TextArea
            id="postPayload"
            placeholder='{"content": "{{content}}", "metadata": {{metadata}}}'
            rows={5}
            title="POST Payload Template (JSON)"
            value={currentAction?.deliverySettings?.postPayload || ''}
            onChange={e =>
              handleFieldChange('deliverySettings', {
                ...currentAction?.deliverySettings,
                url: currentAction?.deliverySettings?.url || '',
                postPayload: e.target.value,
              })
            }
          />
        )}

        {/* Operator Name Input - Feature flagged */}
        {isAdvancedSettingsEnabled && (
          <TextInput
            id="operatorName"
            label="Operator Name"
            placeholder="Enter operator name"
            value={currentAction?.operatorName || ''}
            onChange={value => handleFieldChange('operatorName', value)}
          />
        )}
      </div>
    </Dialog>
  );
}
